import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { useWorkItemProjects } from "@/hooks/queries/use-work-items";
import { usePageTitle } from "@/hooks/use-page-title";
import { canAnywhere, canOn, useCan, useRoleGranters, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { getAdoPatErrorCode } from "@/lib/api/work-items";
import { writeLastProject } from "@/lib/grooming/last-project";
import { groomingPath, projectFromPath, projectPath, viewFromPath } from "@/lib/grooming/paths";
import { joinNames } from "@/lib/toolbox/join-names";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

/**
 * The /grooming route group (epic PI-SPEC-SW-E279, feature PI-SPEC-SW-1390).
 *
 * These pages are the SpecTree Grooming UI in a browser AND in the SpecTree
 * Toolbox desktop app ("one review screen, two places"); the app only adds
 * the runner bridge. Layout in a browser: project select (projects with
 * grooming set up), tabs Backlog / Session / One item / Project (Project
 * only with the `map-project` capability on the project, E283; Repos before
 * PKG-PM-F), then the page. Inside SpecTree
 * Toolbox (epic PI-SPEC-SW-E280) the Toolbox frame's title bar and icon rail
 * replace the select and the tabs, so only the page renders.
 */

const selectClassName =
  "h-8 rounded-lg border border-[#27272A] bg-[#111113] px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

interface Tab {
  page: "backlog" | "session" | "one" | "project";
  label: string;
}

const TABS: readonly Tab[] = [
  { page: "backlog", label: "Backlog" },
  { page: "session", label: "Session" },
  { page: "one", label: "One item" },
];

/** The PM's project setup (PKG-PM-F, UX-PM-15): replaced the Repos tab. */
const PROJECT_TAB: Tab = { page: "project", label: "Project" };

function Loading() {
  return (
    <div className="flex min-h-[300px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/**
 * Setup when no project has grooming yet: pick an ADO project and add its
 * repos on Project → Repositories (PKG-PM-F). Saving the repositories needs `manage-repos` (Admins, epic
 * PI-SPEC-SW-E288), so only the projects the caller may set up that way are
 * listed; an Admin may set up any.
 */
function SetUpProject() {
  const navigate = useNavigate();
  const adoProjects = useWorkItemProjects();
  const { data: access } = useToolboxAccess();
  const [project, setProject] = useState("");
  const mappable = (adoProjects.data ?? []).filter((p) => canOn(access, p.name, "manage-repos"));

  return (
    <form
      className="flex flex-wrap items-center gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (project) void navigate(projectPath(project, "repositories"));
      }}
    >
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        ADO project
        <select
          aria-label="ADO project to set up"
          className={selectClassName}
          value={project}
          onChange={(event) => {
            setProject(event.target.value);
          }}
        >
          <option value="">Choose a project</option>
          {mappable.map((p) => (
            <option key={p.id} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" size="sm" disabled={!project}>
        Set up its repos
      </Button>
    </form>
  );
}

/** "An Admin sets…", naming the Admins when SpecTree knows them (as Setup's Ask for a role does). */
export function adminSetsUpText(adminNames: readonly string[]): string {
  return `An Admin sets a project up by adding the repositories Claude reads for it. To set one up, ask ${joinNames(adminNames, "or", "an Admin")}.`;
}

function AdminSetsUp() {
  const granters = useRoleGranters();
  return (
    <p className="text-sm text-muted-foreground" data-testid="grooming-admin-sets-up">
      {adminSetsUpText((granters.data?.admins ?? []).map((admin) => admin.name))}
    </p>
  );
}

/** `/grooming`: open the first project with grooming set up. */
export function GroomingIndexPage() {
  usePageTitle("Grooming");
  const { data: access } = useToolboxAccess();
  const projects = useGroomingProjects();
  // Saving a project's repositories is Admin only (`manage-repos`), so only they get the set-up flow.
  const maySetUp = canAnywhere(access, "manage-repos");
  const patError = getAdoPatErrorCode(projects.error);

  if (projects.isLoading) return <Loading />;

  const first = projects.data?.[0];
  if (first) return <Navigate to={groomingPath(first.project, "backlog")} replace />;

  return (
    <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
      <h1 className={cn("flex items-center gap-2", TOOLBOX_PAGE_TITLE)}>
        <ListChecks className="h-6 w-6 text-primary" aria-hidden="true" />
        Grooming
      </h1>
      {patError ? (
        <AdoPatCallout code={patError} />
      ) : projects.isError ? (
        <p className="text-sm text-destructive" role="alert">
          The grooming projects could not be loaded. Try again in a moment.
        </p>
      ) : (
        <div className="glass-card space-y-3 rounded-[10px] p-5" data-testid="grooming-no-projects">
          <p className="text-sm">No project is set up for grooming yet.</p>
          {maySetUp ? (
            <>
              <p className="text-sm text-muted-foreground">
                Choose the ADO project, then add the repositories Claude reads for it.
              </p>
              <SetUpProject />
            </>
          ) : (
            <AdminSetsUp />
          )}
        </div>
      )}
    </div>
  );
}

/** `/grooming/:project/*`: project select, tabs and the page (only the page inside SpecTree Toolbox). */
export function GroomingLayout() {
  const { project = "" } = useParams();
  const navigate = useNavigate();
  const inToolbox = useToolboxBridge() !== null;
  const { pathname } = useLocation();
  const activeView = viewFromPath(pathname);
  // The last project on this computer (feature PI-SPEC-SW-1495), for Toolbox home and the palette.
  const shownProject = projectFromPath(pathname);
  useEffect(() => {
    if (shownProject) writeLastProject(shownProject);
  }, [shownProject]);
  const projects = useGroomingProjects();
  const mayMap = useCan(project, "map-project").allowed;
  const tabs = mayMap ? [...TABS, PROJECT_TAB] : TABS;

  // The current project stays selectable while an Admin sets it up.
  const options = useMemo(() => {
    const names = (projects.data ?? []).map((p) => p.project);
    return names.some((name) => name.toLowerCase() === project.toLowerCase()) ? names : [project, ...names];
  }, [projects.data, project]);

  return (
    <div className="w-full space-y-4 px-4 py-5" data-testid="grooming-layout">
      {/* items/:id is the backlog with the item's reading pane (E280), so the tabs stay. */}
      {!inToolbox && (
        <div className="flex flex-wrap items-center gap-3.5 border-b border-border pb-3">
          <label className="sr-only" htmlFor="grooming-project">
            Project
          </label>
          <select
            id="grooming-project"
            aria-label="Project"
            className={selectClassName}
            value={project}
            onChange={(event) => {
              void navigate(groomingPath(event.target.value, "backlog"));
            }}
          >
            {options.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <span className="h-5 w-px bg-border" aria-hidden="true" />
          <nav aria-label="Grooming sections" className="flex gap-1">
            {tabs.map((tab) => (
              <Link
                key={tab.page}
                to={groomingPath(project, tab.page)}
                aria-current={activeView === tab.page ? "page" : undefined}
                className={cn(
                  "rounded-[7px] px-3 py-1.5 text-sm font-medium transition-colors",
                  activeView === tab.page ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
      <Outlet />
    </div>
  );
}
